/** Named Neon connection env vars used for db sync, compare, and copy. */
export type NeonDbEnvVar = 'NEON_URL_DEV' | 'NEON_URL_PREV' | 'NEON_URL_PROD';

/** Local and remote Neon env vars for an environment. */
export type DbSyncEnvPair = {
  readonly localDbEnvVar: NeonDbEnvVar;
  readonly remoteDbEnvVar: NeonDbEnvVar;
};

/** Optional overrides for sync, compare, and copy. */
export type DbSyncEnvParams = {
  /** Local database env var. Defaults by environment. */
  localEnv?: NeonDbEnvVar;
  /** Remote database env var. Defaults by environment. */
  remoteEnv?: NeonDbEnvVar;
  /** When true, delete local rows missing from remote during sync. Defaults by environment. */
  fromMaster?: boolean;
};

/** Resolved Neon env vars for sync, compare, and copy. */
export type ResolvedDbSyncEnv = {
  readonly localDbEnvVar: NeonDbEnvVar;
  readonly remoteDbEnvVar: NeonDbEnvVar;
  readonly fromMaster: boolean;
  readonly remoteLabel: string;
};

/**
 * Human-readable label for a Neon env var (menu titles, confirm dialogs).
 * @param envVar Neon URL env var name.
 * @returns Short source label such as Dev, Prev, or Prod.
 */
export function neonDbEnvVarToLabel(envVar: NeonDbEnvVar): string {
  switch (envVar) {
    case 'NEON_URL_DEV':
      return 'Dev';
    case 'NEON_URL_PREV':
      return 'Prev';
    case 'NEON_URL_PROD':
      return 'Prod';
  }
}

/**
 * Whether the app runs in local development (not Vercel preview or production).
 * @param vercelEnv Value of `NEXT_PUBLIC_VERCEL_ENV`.
 * @returns True when local-only db sync actions should be shown.
 */
export function isLocalDevelopmentEnvironment(vercelEnv: string | undefined): boolean {
  return vercelEnv !== 'production' && vercelEnv !== 'preview';
}

/**
 * Default local vs remote Neon env vars for the current environment.
 * @param vercelEnv Value of `NEXT_PUBLIC_VERCEL_ENV`.
 * @returns Local and remote env var names for this environment.
 */
export function resolveDbSyncEnvPairForEnvironment(vercelEnv: string | undefined): DbSyncEnvPair {
  if (vercelEnv === 'production') {
    return { localDbEnvVar: 'NEON_URL_PROD', remoteDbEnvVar: 'NEON_URL_DEV' };
  }
  if (vercelEnv === 'preview') {
    return { localDbEnvVar: 'NEON_URL_PREV', remoteDbEnvVar: 'NEON_URL_DEV' };
  }
  return { localDbEnvVar: 'NEON_URL_DEV', remoteDbEnvVar: 'NEON_URL_PROD' };
}

/**
 * Whether sync-database should delete local rows missing from remote.
 * @param vercelEnv Value of `NEXT_PUBLIC_VERCEL_ENV`.
 * @returns True in preview and production environments.
 */
export function resolveSyncFromMasterDefault(vercelEnv: string | undefined): boolean {
  return vercelEnv === 'preview' || vercelEnv === 'production';
}

/**
 * Resolves local and remote Neon env vars plus sync delete behavior.
 * @param params Optional overrides from the Inngest event, admin UI, or API query.
 * @param vercelEnv Value of `NEXT_PUBLIC_VERCEL_ENV`.
 * @returns Resolved db sync env options.
 */
export function resolveDbSyncEnv(
  params: DbSyncEnvParams,
  vercelEnv: string | undefined,
): ResolvedDbSyncEnv {
  const defaults = resolveDbSyncEnvPairForEnvironment(vercelEnv);
  const localDbEnvVar = params.localEnv ?? defaults.localDbEnvVar;
  const remoteDbEnvVar = params.remoteEnv ?? defaults.remoteDbEnvVar;
  const fromMaster = params.fromMaster ?? resolveSyncFromMasterDefault(vercelEnv);
  const remoteLabel = neonDbEnvVarToLabel(remoteDbEnvVar);

  return { localDbEnvVar, remoteDbEnvVar, fromMaster, remoteLabel };
}

/**
 * Default remote source label for the standard sync action in the current environment.
 * @param vercelEnv Value of `NEXT_PUBLIC_VERCEL_ENV`.
 * @returns Source label for the default remote database.
 */
export function getDefaultSyncDatabaseSourceLabel(vercelEnv: string | undefined): string {
  return resolveDbSyncEnv({}, vercelEnv).remoteLabel;
}

/**
 * Admin menu / button title for a sync-from-remote action.
 * @param sourceLabel Short remote branch label (Dev, Prev, Prod).
 * @returns Menu title such as "Sync DB from Dev".
 */
export function getSyncDatabaseMenuTitle(sourceLabel: string): string {
  return `Sync DB from ${sourceLabel}`;
}

/**
 * @param value Query string value to validate.
 * @returns True when value is a supported Neon env var name.
 */
export function isNeonDbEnvVar(value: unknown): value is NeonDbEnvVar {
  return value === 'NEON_URL_DEV' || value === 'NEON_URL_PREV' || value === 'NEON_URL_PROD';
}

/**
 * Parses optional db sync env overrides from a Next.js API query object.
 * @param query Request query object.
 * @returns Parsed overrides for {@link resolveDbSyncEnv}.
 */
export function parseDbSyncEnvParams(
  query: Record<string, string | string[] | undefined>,
): DbSyncEnvParams {
  const params: DbSyncEnvParams = {};
  const localEnv = query.localEnv;
  const remoteEnv = query.remoteEnv;
  const fromMaster = query.fromMaster;

  if (typeof localEnv === 'string' && isNeonDbEnvVar(localEnv)) {
    params.localEnv = localEnv;
  }
  if (typeof remoteEnv === 'string' && isNeonDbEnvVar(remoteEnv)) {
    params.remoteEnv = remoteEnv;
  }
  if (fromMaster === 'true') {
    params.fromMaster = true;
  } else if (fromMaster === 'false') {
    params.fromMaster = false;
  }

  return params;
}

/**
 * Appends query params to an API path, preserving any existing query string.
 * @param path Base API path, optionally with existing query params.
 * @param params Query params to append or replace.
 * @returns Path with merged query string.
 */
export function appendQueryParams(
  path: string,
  params: Record<string, string | undefined>,
): string {
  const questionMarkIndex = path.indexOf('?');
  const pathname = questionMarkIndex === -1 ? path : path.slice(0, questionMarkIndex);
  const existingQuery = questionMarkIndex === -1 ? undefined : path.slice(questionMarkIndex + 1);
  const searchParams = new URLSearchParams(existingQuery ?? '');
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined) {
      searchParams.set(key, value);
    }
  }
  const query = searchParams.toString();
  return query ? `${pathname}?${query}` : pathname;
}

/**
 * Appends db sync env query params to an API path.
 * @param path Base API path.
 * @param params Optional env overrides to encode.
 * @returns Path with query string when params are present.
 */
export function appendDbSyncEnvQuery(path: string, params: DbSyncEnvParams): string {
  const searchParams = new URLSearchParams();
  if (params.localEnv) {
    searchParams.set('localEnv', params.localEnv);
  }
  if (params.remoteEnv) {
    searchParams.set('remoteEnv', params.remoteEnv);
  }
  if (params.fromMaster !== undefined) {
    searchParams.set('fromMaster', String(params.fromMaster));
  }
  const query = searchParams.toString();
  return query ? `${path}?${query}` : path;
}
